export type LogicalOperator = 'and' | 'or';
export type OperatorValueMode = 'single' | 'multi' | 'boolean';

export const OPERATOR_CONFIG: Array<{
  label: string;
  value: string;
  mode: OperatorValueMode;
}> = [
  { label: '=', value: 'eq', mode: 'single' },
  { label: '!=', value: 'ne', mode: 'single' },
  { label: '>', value: 'gt', mode: 'single' },
  { label: '>=', value: 'gte', mode: 'single' },
  { label: '<', value: 'lt', mode: 'single' },
  { label: '<=', value: 'lte', mode: 'single' },
  { label: 'In', value: 'in', mode: 'multi' },
  { label: 'Not in', value: 'nin', mode: 'multi' },
  { label: 'Exists', value: 'exists', mode: 'boolean' }
];

export const getOperatorValueMode = (operator: string): OperatorValueMode => {
  return (
    OPERATOR_CONFIG.find(config => config.value === operator)?.mode ?? 'single'
  );
};

let nodeIdCounter = 0;
const buildNodeId = () => `metadata-filter-node-${nodeIdCounter++}`;

export type MetadataFilterConditionValue = string[];

export type MetadataFilterCondition = {
  id: string;
  type: 'condition';
  field: string;
  operator: string;
  value: MetadataFilterConditionValue;
};

export type MetadataFilterGroup = {
  id: string;
  type: 'group';
  operator: LogicalOperator;
  children: MetadataFilterNode[];
};

export type MetadataFilterNode = MetadataFilterCondition | MetadataFilterGroup;

export type MetadataFilterTree = MetadataFilterGroup | null;

const isGroupNode = (node: MetadataFilterNode): node is MetadataFilterGroup =>
  node.type === 'group';

type BackendLeaf = {
  type?: 'leaf';
  field?: string | null;
  operator?: string | null;
  op?: string | null;
  value?: unknown;
};

type BackendGroup = {
  type?: 'group';
  operator?: string | null;
  filters?: Array<BackendLeaf | BackendGroup>;
};

export type LegacySimpleFilter = {
  field?: string | null;
  operator?: string | null;
  op?: string | null;
  value?: unknown;
};

export type BackendExpression = BackendLeaf | BackendGroup | null | undefined;
export type BackendInput =
  | BackendExpression
  | LegacySimpleFilter[]
  | null
  | undefined;

export const createEmptyCondition = (): MetadataFilterCondition => ({
  id: buildNodeId(),
  type: 'condition',
  field: '',
  operator: '',
  value: []
});

export const createEmptyGroup = (
  operator: LogicalOperator = 'and'
): MetadataFilterGroup => ({
  id: buildNodeId(),
  type: 'group',
  operator,
  children: [createEmptyCondition()]
});

const toValueArray = (value: unknown): string[] => {
  if (value == null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .map(item => (item == null ? '' : String(item).trim()))
      .filter(Boolean);
  }

  const text = String(value).trim();
  return text ? [text] : [];
};

const toConditionNode = (leaf: BackendLeaf): MetadataFilterCondition | null => {
  if (!leaf || typeof leaf !== 'object') {
    return null;
  }

  const field = (leaf.field ?? '').toString().trim();
  const operator = (leaf.operator ?? leaf.op ?? '').toString().trim();
  const value = toValueArray(leaf.value);

  if (!field || !operator || value.length === 0) {
    return null;
  }

  return {
    id: buildNodeId(),
    type: 'condition',
    field,
    operator,
    value
  };
};

const toGroupNode = (
  group: BackendGroup | LegacySimpleFilter[]
): MetadataFilterGroup | null => {
  if (Array.isArray(group)) {
    const children = group
      .map(child => toConditionNode(child as BackendLeaf))
      .filter(Boolean) as MetadataFilterNode[];

    if (children.length === 0) {
      return null;
    }

    return {
      id: buildNodeId(),
      type: 'group',
      operator: 'and',
      children
    };
  }

  if (!group || typeof group !== 'object') {
    return null;
  }

  const operator =
    (group.operator === 'or' || group.operator === 'and'
      ? group.operator
      : 'and') ?? 'and';

  const children = (group.filters ?? [])
    .map(child => parseExpression(child))
    .filter(Boolean) as MetadataFilterNode[];

  if (children.length === 0) {
    return null;
  }

  return {
    id: buildNodeId(),
    type: 'group',
    operator,
    children
  };
};

const isBackendNode = (value: unknown): value is BackendLeaf | BackendGroup => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const { type } = value as { type?: string };

  return typeof type === 'string';
};

export const parseExpression = (
  expression: BackendInput
): MetadataFilterNode | null => {
  if (!expression) {
    return null;
  }

  if (Array.isArray(expression)) {
    const backendNodes = expression.filter(isBackendNode);

    if (backendNodes.length === expression.length && backendNodes.length > 0) {
      const children = backendNodes
        .map(node => parseExpression(node))
        .filter(Boolean) as MetadataFilterNode[];

      if (children.length === 0) {
        return null;
      }

      if (children.length === 1) {
        return children[0];
      }

      return {
        id: buildNodeId(),
        type: 'group',
        operator: 'and',
        children
      };
    }

    return toGroupNode(expression);
  }

  if (typeof expression !== 'object') {
    return null;
  }

  const nodeType = (expression as { type?: string }).type?.toLowerCase();

  if (nodeType === 'leaf') {
    return toConditionNode(expression as BackendLeaf);
  }

  if (nodeType === 'group') {
    return toGroupNode(expression as BackendGroup);
  }

  // fallback: try interpreting as leaf first
  return toConditionNode(expression as BackendLeaf);
};

export const parseMetadataFiltersFromBackend = (
  expression: BackendInput
): MetadataFilterTree => {
  const parsedNode = parseExpression(expression);

  if (!parsedNode) {
    return createEmptyGroup();
  }

  if (parsedNode.type === 'group') {
    return parsedNode;
  }

  return {
    id: buildNodeId(),
    type: 'group',
    operator: 'and',
    children: [parsedNode]
  };
};

const normalizeCondition = (
  node: MetadataFilterCondition
): MetadataFilterCondition => ({
  ...node,
  field: node.field.trim(),
  operator: node.operator.trim(),
  value: node.value.map(value => value.trim()).filter(Boolean)
});

const pruneNode = (node: MetadataFilterNode): MetadataFilterNode | null => {
  if (node.type === 'condition') {
    const normalized = normalizeCondition(node);
    if (
      !normalized.field ||
      !normalized.operator ||
      normalized.value.length === 0
    ) {
      return null;
    }

    return normalized;
  }

  const children = node.children
    .map(child => pruneNode(child))
    .filter(Boolean) as MetadataFilterNode[];

  if (children.length === 0) {
    return null;
  }

  return {
    ...node,
    children
  };
};

const ensureGroupChildren = (
  group: MetadataFilterGroup
): MetadataFilterGroup => {
  const children =
    group.children.length > 0
      ? group.children.map(child => ensureNode(child))
      : [createEmptyCondition()];

  return {
    ...group,
    children
  };
};

const ensureNode = (node: MetadataFilterNode): MetadataFilterNode => {
  if (node.type === 'group') {
    return ensureGroupChildren(node);
  }

  return { ...node };
};

export const normalizeMetadataFilterTree = (
  tree: MetadataFilterTree
): MetadataFilterTree => {
  if (!tree) {
    return createEmptyGroup();
  }

  const ensured = ensureNode(tree);

  if (ensured.type === 'group') {
    return ensured;
  }

  const rootGroup = createEmptyGroup();
  return {
    ...rootGroup,
    children: [ensured]
  };
};

const serializeConditionValue = (
  operator: string,
  value: string[]
): string | string[] | boolean => {
  const mode = getOperatorValueMode(operator);

  if (mode === 'boolean') {
    return value[0] === 'false' ? false : true;
  }

  if (mode === 'multi') {
    return value;
  }

  return value[0] ?? '';
};

export const buildMetadataFilterExpression = (
  tree: MetadataFilterTree
): BackendExpression | null => {
  if (!tree) {
    return null;
  }

  const pruned = pruneNode(tree);

  if (!pruned) {
    return null;
  }

  const serialized = serializeNodeWithExpansion(pruned);

  if (!serialized) {
    return null;
  }

  return serialized;
};

export const flattenTreeToLegacyFilters = (
  tree: MetadataFilterTree
): LegacySimpleFilter[] => {
  const normalized = normalizeMetadataFilterTree(tree ?? null);
  const pruned = pruneNode(normalized as MetadataFilterNode);

  if (!pruned) {
    return [];
  }

  const collect = (node: MetadataFilterNode): LegacySimpleFilter[] => {
    if (node.type === 'condition') {
      const normalizedCondition = normalizeCondition(node);
      return [
        {
          field: normalizedCondition.field,
          operator: normalizedCondition.operator,
          op: normalizedCondition.operator,
          value: serializeConditionValue(
            normalizedCondition.operator,
            normalizedCondition.value
          )
        }
      ];
    }

    return node.children.flatMap(child => collect(child));
  };

  return collect(pruned);
};

// Simplified flat structure for UI
export type FlatCondition = {
  id: string;
  field: string;
  operator: string;
  value: string[];
};

export type FlatConditionWithOperator = {
  condition: FlatCondition;
  operator?: LogicalOperator; // Operator before this condition (Or/And)
};

// Convert tree to flat list for simplified UI
export const treeToFlatConditions = (
  tree: MetadataFilterTree
): FlatConditionWithOperator[] => {
  if (!tree || tree.type !== 'group') {
    return [];
  }

  const isCollapsibleEqGroup = (group: MetadataFilterGroup): boolean => {
    if (group.operator !== 'and' || group.children.length < 2) {
      return false;
    }

    const firstChild = group.children[0];
    if (
      firstChild.type !== 'condition' ||
      firstChild.operator !== 'eq' ||
      firstChild.value.length !== 1
    ) {
      return false;
    }

    const targetField = firstChild.field;
    return group.children.every(
      child =>
        child.type === 'condition' &&
        child.operator === 'eq' &&
        child.value.length === 1 &&
        child.field === targetField
    );
  };

  const flatten = (
    node: MetadataFilterNode,
    parentOperator?: LogicalOperator
  ): FlatConditionWithOperator[] => {
    if (node.type === 'condition') {
      return [
        {
          condition: {
            id: node.id,
            field: node.field,
            operator: node.operator,
            value: node.value
          },
          operator: parentOperator
        }
      ];
    }

    if (isCollapsibleEqGroup(node)) {
      const mergedValues = node.children.flatMap(child =>
        child.type === 'condition' ? child.value : []
      );

      const firstChild = node.children[0] as MetadataFilterCondition;

      return [
        {
          condition: {
            id: buildNodeId(),
            field: firstChild.field,
            operator: 'eq',
            value: mergedValues
          },
          operator: parentOperator
        }
      ];
    }

    return node.children.flatMap((child, idx) => {
      const childOperator = idx === 0 ? parentOperator : node.operator;
      return flatten(child, childOperator);
    });
  };

  const flat = flatten(tree);

  // Collapse consecutive "eq" conditions with the same field and AND operator
  const collapsed: FlatConditionWithOperator[] = [];
  type EqGroup = {
    field: string;
    values: string[];
    operator?: LogicalOperator;
  };
  let currentEqGroup: EqGroup | null = null;

  const flushEqGroup = (group: EqGroup) => {
    collapsed.push({
      condition: {
        id: buildNodeId(),
        field: group.field,
        operator: 'eq',
        value: group.values
      },
      operator: group.operator
    });
  };

  flat.forEach(item => {
    const { condition, operator } = item;

    // Check if this is an "eq" condition that can be collapsed
    if (
      condition.operator === 'eq' &&
      condition.value.length === 1 &&
      operator === 'and'
    ) {
      if (
        currentEqGroup &&
        currentEqGroup.field === condition.field &&
        currentEqGroup.operator === operator
      ) {
        // Add to current group
        currentEqGroup.values.push(condition.value[0]);
      } else {
        // Save previous group if exists
        if (currentEqGroup) {
          flushEqGroup(currentEqGroup);
        }
        // Start new group
        currentEqGroup = {
          field: condition.field,
          values: [condition.value[0]],
          operator
        };
      }
    } else {
      // Save previous group if exists
      if (currentEqGroup) {
        flushEqGroup(currentEqGroup);
        currentEqGroup = null;
      }
      // Add current item as-is
      collapsed.push(item);
    }
  });

  // Save last group if exists
  if (currentEqGroup) {
    flushEqGroup(currentEqGroup);
  }

  return collapsed.length > 0 ? collapsed : flat;
};

// Convert flat list back to tree structure
export const flatConditionsToTree = (
  flatConditions: FlatConditionWithOperator[]
): MetadataFilterTree => {
  const sanitizeCondition = (condition: FlatCondition): FlatCondition => ({
    id: condition.id,
    field: condition.field?.trim() ?? '',
    operator: condition.operator?.trim() ?? '',
    value: condition.value.map(v => v.trim()).filter(Boolean)
  });

  const validConditions = flatConditions
    .map(item => ({
      operator:
        item.operator ?? (item === flatConditions[0] ? undefined : 'and'),
      condition: sanitizeCondition(item.condition)
    }))
    .filter(
      item =>
        item.condition.field &&
        item.condition.operator &&
        item.condition.value.length > 0
    );

  if (validConditions.length === 0) {
    return createEmptyGroup();
  }

  const buildConditionNodeFromFlat = (
    condition: FlatCondition
  ): MetadataFilterCondition => ({
    id: condition.id,
    type: 'condition',
    field: condition.field,
    operator: condition.operator,
    value: condition.value
  });

  let expression: MetadataFilterNode | null = null;

  validConditions.forEach((item, index) => {
    const conditionNode = buildConditionNodeFromFlat(item.condition);

    if (index === 0) {
      expression = conditionNode;
      return;
    }

    const operator: LogicalOperator = item.operator === 'or' ? 'or' : 'and';

    expression = {
      id: buildNodeId(),
      type: 'group',
      operator,
      children: [expression as MetadataFilterNode, conditionNode]
    };
  });

  if (!expression) {
    return createEmptyGroup();
  }

  if (isGroupNode(expression)) {
    return expression;
  }

  return {
    id: buildNodeId(),
    type: 'group',
    operator: 'and',
    children: [expression]
  };
};

// Expand "=" operator with multiple values into multiple conditions with AND
const expandCondition = (
  condition: MetadataFilterCondition
): MetadataFilterCondition[] => {
  // If operator is "eq" and has multiple values, expand to multiple conditions
  if (condition.operator === 'eq' && condition.value.length > 1) {
    return condition.value.map(value => ({
      ...condition,
      id: buildNodeId(),
      value: [value]
    }));
  }

  return [condition];
};

// Update serializeNode to expand "=" with arrays
const serializeNodeWithExpansion = (
  node: MetadataFilterNode
): BackendExpression | null => {
  if (node.type === 'condition') {
    const normalized = normalizeCondition(node);

    // Validate condition has required fields
    if (
      !normalized.field ||
      !normalized.operator ||
      normalized.value.length === 0
    ) {
      return null;
    }

    // Expand "=" with multiple values
    const expanded = expandCondition(normalized);

    if (expanded.length === 1) {
      // Single condition - serialize normally
      const serializedValue = serializeConditionValue(
        normalized.operator,
        normalized.value
      );

      // Ensure value is not empty for non-boolean operators
      if (
        normalized.operator !== 'exists' &&
        (serializedValue === '' || serializedValue === null)
      ) {
        return null;
      }

      return {
        type: 'leaf',
        field: normalized.field,
        op: normalized.operator,
        value: serializedValue
      };
    }

    // Multiple conditions - wrap in AND group
    const children = expanded
      .map(cond => {
        const serializedValue = serializeConditionValue(
          cond.operator,
          cond.value
        );

        // Ensure value is not empty for non-boolean operators
        if (
          cond.operator !== 'exists' &&
          (serializedValue === '' || serializedValue === null)
        ) {
          return null;
        }

        return {
          type: 'leaf' as const,
          field: cond.field,
          operator: cond.operator,
          op: cond.operator,
          value: serializedValue
        };
      })
      .filter(Boolean) as BackendLeaf[];

    if (children.length === 0) {
      return null;
    }

    if (children.length === 1) {
      // If only one valid child after filtering, return it as a leaf
      const singleChild = children[0];
      // Remove operator field, keep only op for single leaf conditions
      return {
        type: 'leaf',
        field: singleChild.field,
        op: singleChild.op,
        value: singleChild.value
      };
    }

    return {
      type: 'group',
      operator: 'and',
      filters: children
    };
  }

  const children = node.children
    .map(child => serializeNodeWithExpansion(child))
    .filter(Boolean);

  if (children.length === 0) {
    return null;
  }

  if (children.length === 1) {
    // If only one child, return it directly (unwrap the group)
    const singleChild = children[0];
    // If it's a leaf, ensure it only has op field, not operator
    if (
      singleChild &&
      typeof singleChild === 'object' &&
      'type' in singleChild &&
      singleChild.type === 'leaf'
    ) {
      return {
        type: 'leaf',
        field: singleChild.field,
        op: singleChild.op,
        value: singleChild.value
      };
    }
    return singleChild;
  }

  return {
    type: 'group',
    operator: node.operator,
    filters: children as BackendGroup['filters']
  };
};
